import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Chip from '@mui/material/Chip'
import Popper from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import { ClickAwayListener } from '@mui/base/ClickAwayListener'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useMemo, memo, useCallback } from 'react'
import './MarkinVariants.css'
import { useMarkingContext } from '../MarkingContext/MarkingContext'

const MAX_OPTIONS = 10

const MarkingMenu = memo(({ id, marking, studentId }) => {
  const { ratings, onUpdateMarkingStudent, specialCircumstances, setCurrentAssessmentArea } =
    useMarkingContext()
  const {
    assessmentAreaLevels,
    level,
    elementId,
    subElementId,
    curriculumId,
    assessmentAreaId,
    assessmentAreaRatingType
  } = marking
  const optionsLevels =
    assessmentAreaLevels.sort((a, b) => a?.position - b?.position)?.map((x) => ({ ...x, isSC: false })) ?? []
  const opSCGeneral = specialCircumstances
    ?.filter((sc) => !sc?.curriculumId)
    .map((x) => ({ ...x, isSC: true }))
  const opSCCurriculum = specialCircumstances
    ?.filter((sc) => sc?.curriculumId === curriculumId)
    .map((x) => ({ ...x, isSC: true }))
  const options = [...optionsLevels, ...opSCCurriculum, ...opSCGeneral]

  const [selectedOption, setSelectedOptions] = useState(level)
  const [anchorEl, setAnchorEl] = useState(null)
  const [open, setOpen] = useState(false)
  const [placement, setPlacement] = useState()
  const [search, setSearch] = useState('')
  const [filteredOptions, setFilteredOptions] = useState(options ?? [])

  const handleClick = (newPlacement) => (event) => {
    event.preventDefault()
    setAnchorEl(event?.currentTarget)
    setOpen(true)
    setPlacement(`${newPlacement}-end`)
    setCurrentAssessmentArea(assessmentAreaId)
  }

  const handleClose = (evt) => {
    evt?.stopPropagation()
    setOpen(false)
    setSelectedOptions(level)
  }

  const handleMenuItemClick = (event, value) => {
    event.stopPropagation()
    const levelValue = options?.filter((x) => !x.isSC).find((option) => option?.code === value)
    onUpdateMarkingStudent({ studentId, elementId, subElementId, level: value, assessmentAreaId }, 'level', {
      ratings,
      level: levelValue,
      assessmentAreaRatingType
    })
    setOpen(false)
  }

  const handleSearch = useCallback(
    (value) => {
      setSearch(value)
      if (value.trim() !== '') {
        const filtered = options?.filter((option) => {
          return option?.code?.toLowerCase().includes(value.toLowerCase())
        })
        setFilteredOptions(filtered)
      } else {
        setFilteredOptions(options)
      }
    },
    [options]
  )

  const getAnswerLabel = useMemo(() => {
    const find = options.find((option) => level === option?.code)

    return {
      value: level,
      isValid: !!find
    }
  }, [options, level])

  const getInputType = useMemo(() => {
    return filteredOptions.map((item) => (
      <MenuItem
        className='marking-menu__content__item'
        key={item?.value}
        onClick={(evt) => handleMenuItemClick(evt, item?.code)}
      >
        <FormControlLabel
          control={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <Radio checked={selectedOption === item?.code} style={{ display: 'none' }} />
              {selectedOption === item?.code && (
                <span style={{ color: 'var(--primary)', marginRight: '8px' }}>
                  <FontAwesomeIcon icon='check' />
                </span>
              )}
            </span>
          }
          label={item?.code}
        />
      </MenuItem>
    ))
  }, [filteredOptions, selectedOption])

  useEffect(() => {
    setSelectedOptions(level)
  }, [level])

  useEffect(() => {
    if (!open) {
      setFilteredOptions(options)
      setSearch('')
      setCurrentAssessmentArea(null)
    }
  }, [open])

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div id={id} aria-describedby={id} onClick={handleClick('bottom')} style={{ position: 'relative' }}>
        <Chip
          style={{ width: 80 }}
          className={[
            'clickeable text-clickeable cursor-pointer my-1',
            level ? '' : 'bg-fourth-orange',
            open ? ' selected-chip ' : '',
            getAnswerLabel.isValid ? '' : ' bg-destructive-foreground text-destructive '
          ].join(' ')}
          label={getAnswerLabel.value}
        />

        <Box sx={{ position: 'relative' }}>
          {open && (
            <Popper open={open} anchorEl={anchorEl} placement={placement} transition className='marking-menu'>
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={100}>
                  <Paper>
                    {options?.length > MAX_OPTIONS && (
                      <Box className='options__search-bar'>
                        <TextField
                          id={`short-levels-${studentId}-${assessmentAreaId}`}
                          autoFocus
                          name='search'
                          value={search ?? ''}
                          placeholder='Search'
                          onChange={(evt) => handleSearch(evt.target.value)}
                          inputProps={{
                            maxLength: 25,
                            spellCheck: false
                          }}
                          style={{ mr: 1, width: '100%', height: '40px' }}
                          className='custom-text-field'
                          variant='outlined'
                        />
                      </Box>
                    )}
                    <div className='marking-menu__content'>{getInputType}</div>
                  </Paper>
                </Fade>
              )}
            </Popper>
          )}
        </Box>
      </div>
    </ClickAwayListener>
  )
})
MarkingMenu.displayName = 'MarkingMenu'

export default MarkingMenu
